import { useAppAuth } from '@/lib/useAppAuth';
import { useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useGuestBanners, useLobbyBanners } from '@/lib/banner/useBanners';
import { BannerSlot } from './BannerSlot';

/**
 * Auth-aware container for the top-of-lobby banner slot (ARC-112). Picks the
 * surface + data hook by auth state, then hands the banners to the
 * presentational {@link BannerSlot}:
 *
 *  - **signed-in lobby** (`isAuthenticated`) → `useLobbyBanners()` (authed
 *    `GET /monetization/banners/lobby`).
 *  - **logged-out lander** (guest) → `useGuestBanners('LANDER', queryParams)`,
 *    the PUBLIC `GET /monetization/banners?surface=LANDER`. The lander banner is
 *    NOT gated behind auth — a guest with no token still sees it. `queryParams`
 *    are the current URL's search params, forwarded to the api for pass-through
 *    campaign targeting.
 *
 * Both hooks are always called (Rules of Hooks); only the branch matching the
 * current auth state is `enabled`, so exactly one request fires.
 */
export function LobbyBannerSlot() {
  const { isAuthenticated } = useAppAuth();
  const { searchParams } = useAppSearchParams();

  const queryParams = Object.fromEntries(searchParams.entries());

  const guest = useGuestBanners('LANDER', queryParams, { query: { enabled: !isAuthenticated } });
  const lobby = useLobbyBanners({ query: { enabled: isAuthenticated } });

  const banners = (isAuthenticated ? lobby.data : guest.data)?.banners ?? [];

  return <BannerSlot banners={banners} />;
}
